/*
 * Consumed shared-endpoint content (SHARED-ENDPOINTS-PLAN.md Phase 3).
 *
 * Content syndicated from another BAM! instance is merged into this tenant's
 * state on every read and is never stored here. Its ids carry the namespace
 * the provider's export stamped on them:
 *
 *   se~<endpointId>~<originalId>
 *
 * `~` rather than `:` because these ids become device cache filenames in mp
 * (plan §4.2). The prefix is the authoritative signal that something is
 * consumed; merged folders additionally carry a `sharedEndpoint` object with
 * the provider's endpoint name for display.
 *
 * The rule this module enforces in the UI: consumed content is READ-ONLY here
 * (browse, preview, publish elsewhere — never edit, move or delete). The API
 * rejects such writes too (`assertNoConsumedMutation` in bam-api-services), so
 * this is about not offering the user a button that would 403.
 */

const NAMESPACE_PREFIX = 'se~'

/**
 * True when an id belongs to consumed shared-endpoint content.
 *
 * @param {string} id folder or asset id
 * @returns {boolean}
 */
export const isConsumedId = (id) =>
  typeof id === 'string' && id.indexOf(NAMESPACE_PREFIX) === 0

/**
 * Provider endpoint id encoded in a consumed id, or null.
 *
 * @param {string} id
 * @returns {string|null}
 */
export const getConsumedEndpointId = (id) => {
  if (!isConsumedId(id)) return null
  const rest = id.slice(NAMESPACE_PREFIX.length)
  const separator = rest.indexOf('~')
  return separator > 0 ? rest.slice(0, separator) : null
}

/**
 * Display metadata stamped on merged folders by the server, when present.
 *
 * @param {object} folder
 * @returns {object|null}
 */
export const getConsumedMeta = (folder) =>
  (folder && folder.sharedEndpoint) || null

/**
 * Asset actions that stay available on consumed content. Everything else is
 * a mutation of the provider's asset, or mints something (a public URL, a
 * crop) that is the provider's call to make, not the consumer's.
 */
export const CONSUMED_ASSET_ACTIONS = ['download', 'share', 'publish']

/**
 * @param {string} action a CardConstants GLOBAL_ACTIONS action key
 * @param {string} assetId
 * @returns {boolean} whether to offer this action for this asset
 */
export const isActionAllowedOnConsumedAsset = (action, assetId) => {
  if (!isConsumedId(assetId)) return true
  return CONSUMED_ASSET_ACTIONS.indexOf(action) > -1
}
